/**
 * Validation schemas for the admin-managed service (systems) catalog.
 * The API validates every create/update body through these; the frontend
 * reuses the inferred input types.
 */

import { z } from "zod";

/** Stable slug: lower-case alnum with dash/underscore separators. */
export const serviceIdSchema = z
  .string()
  .trim()
  .min(1)
  .max(64)
  .regex(
    /^[a-z0-9][a-z0-9_-]*$/,
    "id must be lower-case letters, digits, - or _",
  );

/** Optional 3/6-digit hex colour (e.g. #1F4E8C). */
const colorSchema = z
  .string()
  .trim()
  .regex(/^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/, "color must be a hex value");

const nameSchema = z.string().trim().min(1).max(120);
const descriptionSchema = z.string().trim().max(500);
const logoSchema = z.string().trim().max(2000);
const stringListSchema = z.array(z.string().trim().min(1).max(200)).max(200);

export const createServiceSchema = z
  .object({
    serviceId: serviceIdSchema,
    name: nameSchema,
    description: descriptionSchema.optional(),
    logo: logoSchema.optional(),
    color: colorSchema.optional(),
    options: stringListSchema.default([]),
    fields: stringListSchema.default([]),
    enabled: z.boolean().default(true),
    sortOrder: z.number().int().min(0).max(100000).default(0),
  })
  .strict();

export const updateServiceSchema = z
  .object({
    name: nameSchema.optional(),
    description: descriptionSchema.nullable().optional(),
    logo: logoSchema.nullable().optional(),
    color: colorSchema.nullable().optional(),
    options: stringListSchema.optional(),
    fields: stringListSchema.optional(),
    enabled: z.boolean().optional(),
    sortOrder: z.number().int().min(0).max(100000).optional(),
    /** Optimistic concurrency: the version the client last read. */
    version: z.number().int().nonnegative(),
  })
  .strict();

export type CreateServiceInput = z.infer<typeof createServiceSchema>;
export type UpdateServiceInput = z.infer<typeof updateServiceSchema>;
